import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import { CssBaseline, GlobalStyles } from "@mui/material";

// Import theme utilities
import componentStyleOverrides, {
  getReusableComponentStyles,
} from "./compStyleOverride";
import themePalette from "./palette";
import themeTypography from "./typography";
import colors from "./_themes-vars.module.scss";
import { borderRadius } from "./constant";

// ==============================|| THEME CONTEXT ||============================== //
const ThemeContext = createContext();

/**
 * Custom hook to access theme context
 * @returns {Object} Theme context with mode and toggleColorMode
 * @throws {Error} If used outside of CustomThemeProvider
 */
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(
      "useThemeContext must be used within a CustomThemeProvider"
    );
  }
  return context;
};

/**
 * Custom Theme Provider Component
 *
 * Provides theme context and Material-UI theme with dark/light mode support.
 * Includes localStorage persistence and system preference detection.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Theme provider component
 */
export const CustomThemeProvider = ({ children }) => {
  // ==============================|| THEME STATE ||============================== //
  const [mode, setMode] = useState(() => {
    try {
      // Check localStorage for saved preference
      const savedMode = localStorage.getItem("theme-mode");
      if (savedMode && ["light", "dark"].includes(savedMode)) {
        return savedMode;
      }

      // Fallback to system preference
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        return "dark";
      }

      return "light";
    } catch (error) {
      console.warn("Failed to load theme preference:", error);
      return "light";
    }
  });

  // ==============================|| THEME MODE MANAGEMENT ||============================== //
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          try {
            localStorage.setItem("theme-mode", newMode);
          } catch (error) {
            console.warn("Failed to save theme preference:", error);
          }
          return newMode;
        });
      },
      mode,
      setMode: (newMode) => {
        if (["light", "dark"].includes(newMode)) {
          setMode(newMode);
          try {
            localStorage.setItem("theme-mode", newMode);
          } catch (error) {
            console.warn("Failed to save theme preference:", error);
          }
        }
      },
    }),
    [mode]
  );

  // ==============================|| SYSTEM PREFERENCE LISTENER ||============================== //
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      // Only auto-switch if no manual preference is saved
      const savedMode = localStorage.getItem("theme-mode");
      if (!savedMode) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // ==============================|| THEME CREATION ||============================== //
  const theme = useMemo(() => {
    try {
      const color = colors;
      const isDark = mode === "dark";

      // Create theme options based on mode
      const themeOption = {
        colors: color,
        mode,
        heading: isDark ? color.darkTextTitle : color.grey900,
        paper: isDark ? color.darkPaper : color.paper,
        backgroundDefault: isDark ? color.darkBackground : color.appBackground,
        background: isDark ? color.darkBackground : color.brandPrimaryLight,
        darkTextPrimary: isDark ? color.darkTextPrimary : color.grey700,
        darkTextSecondary: isDark ? color.darkTextSecondary : color.grey500,
        textDark: isDark ? color.darkTextPrimary : color.grey900,
        menuSelected: isDark ? color.darkTextPrimary : color.grey900,
        menuSelectedBack: isDark ? color.darkLevel1 : color.brandPrimaryMain,
        divider: isDark ? color.darkLevel1 : color.grey200,
        customization: {
          navType: mode,
          borderRadius: borderRadius,
        },
      };

      const themeOptions = {
        direction: "ltr",
        palette: {
          ...themePalette(themeOption),
          mode,
        },
        mixins: {
          toolbar: {
            minHeight: "48px",
            padding: "16px",
            "@media (min-width: 600px)": {
              minHeight: "48px",
            },
          },
        },
        typography: {
          htmlFontSize: 18,
          ...themeTypography(themeOption),
        },
        components: componentStyleOverrides(themeOption),
        // Add custom theme properties
        custom: {
          borderRadius: borderRadius,
          componentStyles: getReusableComponentStyles(themeOption), // Add reusable styles here
          shadows: {
            xs: "0 1px 2px rgba(0, 0, 0, 0.05)",
            sm: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
            md: "0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)",
            lg: "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
            xl: "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
          },
        },
      };

      return createTheme(themeOptions);
    } catch (error) {
      console.error("Failed to create theme:", error);
      // Return a fallback theme
      return createTheme({
        palette: {
          mode: "light",
        },
      });
    }
  }, [mode]);

  // ==============================|| RENDER ||============================== //
  return (
    <ThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: {
              fontSize: "18px", // Base font size - scales all rem units proportionally
            },
            body: {
              backgroundColor: theme.palette.background.default,
              transition: "background-color 0.3s ease",
            },
            "*": {
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.grey[400]} transparent`,
            },
            "*::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "*::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}
        />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
